
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Link } from 'react-router-dom';
import { TrendingDown, TrendingUp } from "lucide-react";

interface MetricCardProps {
  icon: React.ReactNode;
  title: string;
  value?: { value: number; trend: 'up' | 'down' | 'none'; percentChange: number } | number | string;
  description?: string;
  gradient?: string;
  textColor?: string;
  isText?: boolean;
  linkTo?: string; // For navigation
}

export function MetricCard({ 
  icon, 
  title, 
  value, 
  description, 
  gradient = "bg-card", 
  textColor = "text-card-foreground",
  isText = false,
  linkTo
}: MetricCardProps) {
  // Render content based on value type
  const renderValue = () => {
    if (value === undefined) {
      return <Skeleton className="h-10 w-24" />;
    }
    
    if (typeof value === 'object' && 'value' in value) {
      return (
        <div className="flex items-end gap-2">
          <div className="text-4xl font-bold">{value.value}</div>
          {value.trend !== 'none' && (
            <Badge 
              variant={value.trend === 'up' ? 'destructive' : 'secondary'} 
              className="mb-1"
            >
              {value.trend === 'up' ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
              {value.percentChange}%
            </Badge>
          )}
        </div>
      );
    }
    
    return <div className="text-4xl font-bold">{value}</div>;
  };
  
  const cardContent = (
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium mb-1">{title}</p>
          {renderValue()}
          {description && (
            <p className="text-xs opacity-80 mt-1">{description}</p>
          )}
        </div>
        <div className={cn("p-3 rounded-full bg-white/20")}>
          {icon}
        </div>
      </div>
    </CardContent>
  );
  
  // Conditional rendering based on whether a link is provided
  if (linkTo) {
    return (
      <Link 
        to={linkTo} 
        className={cn(gradient, "block relative overflow-hidden rounded-xl transition-all hover:-translate-y-1 hover:shadow-lg", textColor)}
      >
        {cardContent}
      </Link>
    );
  }
  
  return (
    <Card className={cn(gradient, "relative overflow-hidden rounded-xl", textColor)}>
      {cardContent}
    </Card>
  );
}
